import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'vl-theme';

const ThemeContext = createContext(null);

const readStoredTheme = () => {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch (e) {
    return null;
  }
};

const systemPrefersDark = () =>
  Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

export function ThemeProvider({ children }) {
  const [explicitTheme, setExplicitTheme] = useState(readStoredTheme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (explicitTheme) root.setAttribute('data-theme', explicitTheme);
    else root.removeAttribute('data-theme');
  }, [explicitTheme]);

  const isDark = explicitTheme ? explicitTheme === 'dark' : systemDark;

  const setTheme = useCallback((next) => {
    setExplicitTheme(next);
    try {
      if (next) window.localStorage.setItem(THEME_STORAGE_KEY, next);
      else window.localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (e) {
      // Storage blocked: the choice lasts for this page view only.
    }
  }, []);

  const toggleTheme = useCallback(() => setTheme(isDark ? 'light' : 'dark'), [isDark, setTheme]);

  const value = useMemo(
    () => ({ theme: isDark ? 'dark' : 'light', isDark, explicitTheme, setTheme, toggleTheme }),
    [isDark, explicitTheme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
